<template>
  <Card
    :title="$t('global.content-delivery-network')"
    :help="{
      header: $t('global.cdn'),
      message: $t('global.cdn-explanation'),
    }"
  >
    <LoadingState v-if="cdnStatus.isLoading" :size="50" />
    <ErrorState v-else-if="cdnStatus.isErrored" />
    <template v-else-if="cdnStatus.isLoaded">
      <div class="mb-3">
        <p>
          <span class="mr-2">{{ $t("global.cdn-included-in-plan") }}</span>
          <a v-if="cdnLink" :href="cdnLink" target="_blank">{{ $t("global.learn-more") }}</a>
        </p>
        <div class="mb-3">
          <LoadingState v-if="eadnStatus.isLoading" :size="50" />
          <ErrorState v-else-if="eadnStatus.isErrored" />
          <CardListContent v-else-if="eadn" :list="getList()" class="pb-3" />
          <div v-if="canEnable" class="pb-2">
            <p>{{ $t("global.enable-cdn-msg") }}</p>
            <Btn @click="enableCdn" class="mb-3">{{ $t("global.enable-cdn") }}</Btn>
          </div>
          <div>
            <CardCollapse :title="$t('global.cdn-usage-details')" @update:toggle="toggleCdnUsage">
              <LoadingState v-if="cdnUsageStatus.isLoading" class="pt-2" />
              <ErrorState v-else-if="cdnUsageStatus.isErrored" class="pt-2" />
              <CardTabledList v-else-if="cdnUsageStatus.isLoaded" :items="parseUsageData()" />
            </CardCollapse>
          </div>
        </div>
      </div>
    </template>
  </Card>
</template>

<script>
import { computed, onMounted, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import Card from "@/components/display/Card";
import CardCollapse from "@/components/display/card-sub-components/CardCollapse";
import CardListContent from "@/components/display/card-sub-components/CardListContent";
import CardTabledList from "@/components/display/card-sub-components/CardTabledList";
import ConfirmationModal from "@/components/display/ConfirmationModal";
import ErrorState from "@/components/display/ErrorState";
import LoadingState from "@/components/display/LoadingState";
import { moneyFormat } from "leatherman-js/utils/formatters/money";
import store from "@/store";

export default {
  name: "Cdn",
  props: {
    plan: Object,
    site: Object,
    namespace: String,
  },
  components: {
    Btn,
    Card,
    CardCollapse,
    CardListContent,
    CardTabledList,
    ErrorState,
    LoadingState,
  },
  setup(props, { root }) {
    const namespace = props.namespace;
    const togglingCdn = ref(false);
    const disabled = ref(false);
    const cdnLink = computed(() => store.getters["company/getCompany"].cdn_info_link);
    const cdn = computed(() => store.getters[`${namespace}/getCdn`]);
    const cdnStatus = computed(() => store.getters[`${namespace}/getCdnStatus`]);
    const eadn = computed(() => store.getters[`${namespace}/getEadn`]);
    const eadnStatus = computed(() => store.getters[`${namespace}/getEadnStatus`]);
    const cdnUsage = computed(() => store.getters[`${namespace}/getCdnUsage`]);
    const cdnUsageStatus = computed(() => store.getters[`${namespace}/getCdnUsageStatus`]);
    const canEnable = computed(() => !props.plan?.isTShirt && cdn.value && !cdn.value.enabled);

    onMounted(() => {
      const siteId = props.site?.id;
      store.dispatch(`${namespace}/getCdn`, siteId);
      store.dispatch(`${namespace}/getEadn`, siteId);
      store.dispatch(`${namespace}/getCdnUsage`, siteId);
    });

    const confirmToggleCdn = () =>
      store
        .dispatch("sites/enableCdn", props.site.id)
        .then(() => {
          store.dispatch("toasts/addToast", {
            variant: "info",
            message: root.$t("toasts.cdn-enabling-success-message"),
          });
        })
        .catch(() => {
          store.dispatch("toasts/addToast", {
            variant: "error",
            message: root.$t("toasts.cdn-enabling-error-message"),
          });
        })
        .finally(() => (togglingCdn.value = false));

    const toggleCdnUsage = (show) => {
      if (show) {
        store.dispatch("sites/getCdnUsage", props.site.id);
      }
    };

    const enableCdn = () => {
      togglingCdn.value = true;
      store.dispatch("modals/open", {
        template: ConfirmationModal,
        data: {
          confirm: confirmToggleCdn,
          errorMessage: root.$t("toasts.cdn-enabling-error-message"),
          message: root.$t("global.cdn-confirm-enable"),
          title: root.$t("global.content-delivery-network"),
        },
      });
    };

    const parseUsageData = () => {
      const { eadn_account: account, usage } = cdnUsage.value;
      const packageInfo = root.$t("global.cdn-package-info", {
        fee: moneyFormat(account?.monthly_fee),
        bandwidth: account?.included_bandwidth,
      });
      const remainingBandwidth = root.$t("global.cdn-remaining-bandwidth-info", {
        remaining: Math.max(account?.included_bandwidth - usage?.used?.total, 0),
      });
      const overage = root.$t("global.cdn-overage-info", {
        cost: moneyFormat(usage?.used?.cost),
        overage: usage?.used?.overage,
        fee: moneyFormat(account?.overage_fee),
      });
      return [
        { key: root.$t("global.base-price"), value: packageInfo },
        { key: root.$t("global.remaining-bandwidth"), value: remainingBandwidth },
        { key: root.$t("global.overage"), value: overage },
      ];
    };

    const getList = () => [
      { label: root.$t("global.cdn-origin"), content: eadn.value?.origin, canCopy: true },
      { label: root.$t("global.cdn-points-to"), content: eadn.value?.points_to, canCopy: true },
    ];

    return {
      canEnable,
      cdn,
      cdnStatus,
      cdnUsage,
      cdnUsageStatus,
      eadn,
      eadnStatus,
      enableCdn,
      cdnLink,
      disabled,
      getList,
      parseUsageData,
      toggleCdnUsage,
      togglingCdn,
    };
  },
};
</script>
